import { useState } from 'react';
import { supabase } from '@/lib/supabase';

interface NotifResult {
  tipo: string;
  destinatario: string;
  assunto: string;
}

interface RunResult {
  success: boolean;
  tipo: string;
  total_notificacoes: number;
  notificacoes: NotifResult[];
  error?: string;
}

const TIPOS_NOTIF = [
  {
    key: 'sem_formacao',
    label: 'Funcionários sem Formação',
    icon: 'ri-user-unfollow-line',
    desc: 'Envia email a funcionários que não têm formação há mais de X meses.',
    color: 'bg-rose-50 text-rose-600 border-rose-200',
    iconBg: 'bg-rose-100',
    hasLimiar: true,
    hasEmail: false,
  },
  {
    key: 'formacao_proxima',
    label: 'Lembrete de Formação Próxima',
    icon: 'ri-calendar-event-line',
    desc: 'Envia lembrete aos participantes de formações que começam em X dias.',
    color: 'bg-amber-50 text-amber-600 border-amber-200',
    iconBg: 'bg-amber-100',
    hasLimiar: false,
    hasDias: true,
    hasEmail: false,
  },
  {
    key: 'acao_atrasada',
    label: 'Ações Atrasadas',
    icon: 'ri-alarm-warning-line',
    desc: 'Envia alerta ao gestor sobre ações planeadas cuja data já passou.',
    color: 'bg-zinc-100 text-zinc-600 border-zinc-200',
    iconBg: 'bg-zinc-200',
    hasLimiar: false,
    hasEmail: true,
  },
];

export default function NotificacoesFormacaoPage() {
  const [limiarMeses, setLimiarMeses] = useState(12);
  const [diasAntecedencia, setDiasAntecedencia] = useState(7);
  const [emailGestor, setEmailGestor] = useState('');
  const [running, setRunning] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, RunResult>>({});
  const [testMode, setTestMode] = useState(true);

  async function runNotificacao(tipo: string) {
    setRunning(tipo);
    try {
      const { data, error } = await supabase.functions.invoke('formacao-notificacoes', {
        body: {
          tipo,
          limiar_meses: limiarMeses,
          dias_antecedencia: diasAntecedencia,
          email_destino: emailGestor || undefined,
        },
      });
      if (error) throw error;
      setResults((prev) => ({ ...prev, [tipo]: data as RunResult }));
    } catch (e) {
      setResults((prev) => ({
        ...prev,
        [tipo]: { success: false, tipo, total_notificacoes: 0, notificacoes: [], error: (e as Error).message },
      }));
    } finally {
      setRunning(null);
    }
  }

  async function runTeste() {
    setRunning('teste');
    try {
      const { data, error } = await supabase.functions.invoke('formacao-notificacoes', {
        body: { tipo: 'teste', limiar_meses: limiarMeses, dias_antecedencia: diasAntecedencia, email_destino: emailGestor || undefined },
      });
      if (error) throw error;
      setResults((prev) => ({ ...prev, teste: data as RunResult }));
    } catch (e) {
      setResults((prev) => ({
        ...prev,
        teste: { success: false, tipo: 'teste', total_notificacoes: 0, notificacoes: [], error: (e as Error).message },
      }));
    } finally {
      setRunning(null);
    }
  }

  return (
    <main className="flex-1 bg-slate-50/60 overflow-auto">
      {/* Header */}
      <div className="bg-white border-b border-zinc-100 px-8 py-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-zinc-900 flex-shrink-0">
              <i className="ri-mail-send-line text-white text-lg" />
            </div>
            <div>
              <span className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest">Formação</span>
              <h1 className="text-[20px] font-bold text-zinc-900 tracking-tight leading-tight">Notificações por Email</h1>
              <p className="text-[12px] text-zinc-400 mt-0.5">Alertas automáticos — o sistema avisa quando há algo a tratar</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 bg-zinc-100 rounded-lg p-1">
              <button
                onClick={() => setTestMode(true)}
                className={`whitespace-nowrap px-3 py-1.5 rounded-md text-[12px] font-semibold transition-all cursor-pointer ${testMode ? 'bg-white text-zinc-900' : 'text-zinc-500 hover:text-zinc-700'}`}
              >
                Modo Teste
              </button>
              <button
                onClick={() => setTestMode(false)}
                className={`whitespace-nowrap px-3 py-1.5 rounded-md text-[12px] font-semibold transition-all cursor-pointer ${!testMode ? 'bg-white text-zinc-900' : 'text-zinc-500 hover:text-zinc-700'}`}
              >
                Produção
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="px-8 py-6 space-y-5">

        {/* Aviso modo teste */}
        {testMode && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 flex items-start gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-100 flex-shrink-0 mt-0.5">
              <i className="ri-test-tube-line text-amber-600 text-sm" />
            </div>
            <div>
              <p className="text-[13px] font-bold text-amber-800">Modo de Teste Ativo</p>
              <p className="text-[12px] text-amber-700 mt-0.5">
                Em modo de teste, os emails <strong>não são enviados</strong> — apenas é mostrado o que seria enviado.
                Para ativar o envio real, é necessário configurar uma chave de API de email (Resend ou SendGrid) nas variáveis de ambiente da edge function.
              </p>
            </div>
          </div>
        )}

        {/* Configurações globais */}
        <div className="bg-white rounded-xl border border-zinc-100 p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-settings-3-line text-zinc-400 text-sm" />
            </div>
            <p className="text-[13px] font-bold text-zinc-800">Configurações de Notificação</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">Limiar — Meses sem Formação</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={1}
                  max={60}
                  value={limiarMeses}
                  onChange={(e) => setLimiarMeses(parseInt(e.target.value) || 12)}
                  className="w-20 px-3 py-2 text-[13px] font-bold border border-zinc-200 rounded-lg bg-white text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-400 text-center"
                />
                <span className="text-[12px] text-zinc-500">meses</span>
              </div>
              <p className="text-[10px] text-zinc-400 mt-1">Alerta quando funcionário não tem formação há X meses</p>
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">Antecedência — Lembrete de Formação</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={1}
                  max={30}
                  value={diasAntecedencia}
                  onChange={(e) => setDiasAntecedencia(parseInt(e.target.value) || 7)}
                  className="w-20 px-3 py-2 text-[13px] font-bold border border-zinc-200 rounded-lg bg-white text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-400 text-center"
                />
                <span className="text-[12px] text-zinc-500">dias</span>
              </div>
              <p className="text-[10px] text-zinc-400 mt-1">Envia lembrete X dias antes do início da formação</p>
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">Email do Gestor de Formação</label>
              <input
                type="email"
                value={emailGestor}
                onChange={(e) => setEmailGestor(e.target.value)}
                placeholder="gestor@camara.pt"
                className="w-full px-3 py-2 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-400"
              />
              <p className="text-[10px] text-zinc-400 mt-1">Para alertas de ações atrasadas e orçamento</p>
            </div>
          </div>
        </div>

        {/* Tipos de notificação */}
        <div className="space-y-4">
          {TIPOS_NOTIF.map((tipo) => {
            const result = results[tipo.key];
            const isRunning = running === tipo.key;
            return (
              <div key={tipo.key} className={`bg-white rounded-xl border overflow-hidden ${tipo.color.split(' ')[2]}`}>
                <div className="p-5 flex items-start gap-4 flex-wrap">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${tipo.iconBg}`}>
                    <i className={`${tipo.icon} text-lg ${tipo.color.split(' ')[1]}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[14px] font-bold text-zinc-900">{tipo.label}</p>
                    <p className="text-[12px] text-zinc-500 mt-0.5">{tipo.desc}</p>
                  </div>
                  <button
                    onClick={() => runNotificacao(tipo.key)}
                    disabled={isRunning || running !== null}
                    className="whitespace-nowrap flex items-center gap-2 bg-zinc-900 text-white text-[13px] font-medium px-4 py-2.5 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                  >
                    {isRunning ? (
                      <><div className="w-4 h-4 flex items-center justify-center"><i className="ri-loader-4-line text-sm animate-spin" /></div>A processar...</>
                    ) : (
                      <><div className="w-4 h-4 flex items-center justify-center"><i className="ri-send-plane-line text-sm" /></div>{testMode ? 'Simular' : 'Enviar'}</>
                    )}
                  </button>
                </div>

                {/* Resultado */}
                {result && (
                  <div className={`border-t px-5 py-4 ${result.success ? 'bg-zinc-50/50' : 'bg-rose-50/50'}`}>
                    {result.error ? (
                      <div className="flex items-center gap-2 text-rose-600">
                        <div className="w-4 h-4 flex items-center justify-center"><i className="ri-error-warning-line text-sm" /></div>
                        <span className="text-[12px] font-semibold">Erro: {result.error}</span>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-checkbox-circle-fill text-sm text-emerald-500" />
                          </div>
                          <span className="text-[12px] font-bold text-zinc-700">
                            {result.total_notificacoes === 0
                              ? 'Nenhuma notificação necessária'
                              : `${result.total_notificacoes} notificação(ões) ${testMode ? 'simulada(s)' : 'enviada(s)'}`}
                          </span>
                        </div>
                        {result.notificacoes.length > 0 && (
                          <div className="space-y-1.5 max-h-48 overflow-y-auto">
                            {result.notificacoes.map((n, i) => (
                              <div key={i} className="bg-white rounded-lg border border-zinc-100 px-3 py-2 flex items-start gap-3">
                                <div className="w-5 h-5 flex items-center justify-center rounded-full bg-zinc-100 flex-shrink-0 mt-0.5">
                                  <i className="ri-mail-line text-zinc-500 text-xs" />
                                </div>
                                <div className="min-w-0">
                                  <p className="text-[11px] font-semibold text-zinc-700 truncate">{n.destinatario}</p>
                                  <p className="text-[10px] text-zinc-400 truncate">{n.assunto}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Teste completo */}
        <div className="bg-white rounded-xl border border-zinc-100 p-5">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="text-[14px] font-bold text-zinc-900">Executar Todos os Alertas</p>
              <p className="text-[12px] text-zinc-500 mt-0.5">Processa todos os tipos de notificação de uma vez</p>
            </div>
            <button
              onClick={runTeste}
              disabled={running !== null}
              className="whitespace-nowrap flex items-center gap-2 bg-zinc-900 text-white text-[13px] font-medium px-5 py-2.5 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer disabled:opacity-50"
            >
              {running === 'teste' ? (
                <><div className="w-4 h-4 flex items-center justify-center"><i className="ri-loader-4-line text-sm animate-spin" /></div>A processar...</>
              ) : (
                <><div className="w-4 h-4 flex items-center justify-center"><i className="ri-play-circle-line text-sm" /></div>Executar Todos</>
              )}
            </button>
          </div>
          {results['teste'] && (
            <div className="mt-4 pt-4 border-t border-zinc-100">
              <p className="text-[12px] font-bold text-zinc-700 mb-2">
                Total: {results['teste'].total_notificacoes} notificação(ões) {testMode ? 'simulada(s)' : 'enviada(s)'}
              </p>
              {results['teste'].notificacoes.length > 0 && (
                <div className="space-y-1.5 max-h-64 overflow-y-auto">
                  {results['teste'].notificacoes.map((n, i) => (
                    <div key={i} className="bg-zinc-50 rounded-lg px-3 py-2 flex items-center gap-3">
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                        n.tipo === 'sem_formacao' ? 'bg-rose-100 text-rose-700' :
                        n.tipo === 'formacao_proxima' ? 'bg-amber-100 text-amber-700' :
                        'bg-zinc-200 text-zinc-600'
                      }`}>{n.tipo}</span>
                      <span className="text-[11px] text-zinc-600 truncate flex-1">{n.destinatario}</span>
                      <span className="text-[10px] text-zinc-400 truncate max-w-[200px]">{n.assunto}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Instruções para envio real */}
        <div className="bg-zinc-50 border border-zinc-200 rounded-xl px-5 py-4">
          <p className="text-[13px] font-bold text-zinc-700 mb-2">Como ativar o envio real de emails</p>
          <ol className="space-y-1.5">
            {[
              'Cria uma conta gratuita em resend.com (até 3.000 emails/mês grátis)',
              'Obtém a chave de API (API Key) no painel do Resend',
              'Adiciona a variável RESEND_API_KEY nas configurações da edge function no Supabase',
              'Descomenta o bloco de código de envio na edge function "formacao-notificacoes"',
              'Configura o domínio de envio (ex: formacao@camara.pt) no Resend',
            ].map((step, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <span className="w-5 h-5 flex items-center justify-center rounded-full bg-zinc-200 text-zinc-600 text-[10px] font-bold flex-shrink-0 mt-0.5">{i + 1}</span>
                <span className="text-[12px] text-zinc-600">{step}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </main>
  );
}
